import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc 
} from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase/config';
import { loginApi } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(() =>
    (typeof window !== 'undefined' && (sessionStorage.getItem('userRole') || localStorage.getItem('userRole'))) || null
  );
  const [loading, setLoading] = useState(true);
  const [apiToken, setApiToken] = useState(null);

  const signInWithGoogle = async (role) => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Call loginApi after successful Google Sign-In
      try {
        const loginCredentials = {
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          uid: user.uid,
          role: role
        };
        
        const apiResponse = await loginApi(loginCredentials);
        
        // Store the token from API response
        if (apiResponse.data &&apiResponse.data.data.LoginResponse.token) {
          const token = apiResponse.data.data.LoginResponse.token;
          const firebaseToken = await user.getIdToken();
          
          // Store authentication data in sessionStorage
          sessionStorage.setItem('token', token);
          sessionStorage.setItem('firebaseToken', firebaseToken);
          sessionStorage.setItem('userRole', role);
          sessionStorage.setItem('uid', user.uid);
          sessionStorage.setItem('displayName', user.displayName);
          sessionStorage.setItem('email', user.email);
          
          setApiToken(token);
        }
        
        console.log('API login successful:', apiResponse.data);
      } catch (apiError) {
        console.warn('API login failed, continuing with Firebase auth:', apiError);
        // Continue with Firebase auth even if API fails
      }
      
      // Check if user exists in Firestore (with error handling)
      let userDoc;
      try {
        userDoc = await getDoc(doc(db, 'users', user.uid));
      } catch (firestoreError) {
        console.warn('Firestore unavailable, creating user without database check:', firestoreError);
        // Continue without Firestore for now
        // Check if admin email for offline mode
        if (user.email && (user.email.includes('admin') || user.email === 'admin@rewards.com')) {
          setUserRole('admin');
        } else {
          setUserRole(role);
        }
        return result;
      }
      
      if (!userDoc.exists()) {
        // Create new user document (with error handling)
        try {
          await setDoc(doc(db, 'users', user.uid), {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            role: role,
            createdAt: new Date(),
            // Customer specific fields
            ...(role === 'customer' && {
              totalPoints: 0,
              amountSpent: 0,
              amountSaved: 0,
              location: null
            }),
            // Business specific fields
            ...(role === 'business' && {
              businessName: user.displayName,
              pointsGiven: 0,
              availablePoints: 1000, // Starting points for business
              totalPurchases: 0,
              totalIncome: 0,
              pointsPerRupee: 1,
              minimumRedemptionPoints: 100,
              discountType: 'none', // 'fixed', 'range', 'none'
              discountValue: 0,
              location: null,
              address: ''
            })
          });
        } catch (createError) {
          console.warn('Failed to create user document in Firestore:', createError);
          // Continue without creating the document
        }
      }
      
      setUserRole(role);
      return result;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUserRole(null);
      setApiToken(null);
      // Clear all authentication data from storage
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('firebaseToken');
      sessionStorage.removeItem('userRole');
      sessionStorage.removeItem('uid');
      sessionStorage.removeItem('displayName');
      sessionStorage.removeItem('email');
      localStorage.removeItem('token');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const updateUserProfile = async (updates) => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), updates);
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        
        // Fetch user role from Firestore with offline handling
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || 'customer');
            // persist for reloads
            try { sessionStorage.setItem('userRole', userData.role || 'customer'); } catch {}
          } else {
            // Fallback to stored role if present
            const storedRole = sessionStorage.getItem('userRole') || localStorage.getItem('userRole');
            setUserRole(storedRole || 'customer');
          }
        } catch (error) {
          // Handle offline mode gracefully
          if (error.code === 'unavailable' || error.message.includes('offline')) {
            console.warn('Firestore offline - using default role');
            // Check if admin email for offline mode
            if (user.email && (user.email.includes('admin') || user.email === 'admin@rewards.com')) {
              setUserRole('admin');
            } else {
              const storedRole = sessionStorage.getItem('userRole') || localStorage.getItem('userRole');
              setUserRole(storedRole || 'customer'); // Use stored role if available
            }
          } else {
            console.error('Error fetching user role:', error);
            const storedRole = sessionStorage.getItem('userRole') || localStorage.getItem('userRole');
            setUserRole(storedRole || 'customer'); // Use stored role on errors
          }
        }
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    apiToken,
    signInWithGoogle,
    logout,
    updateUserProfile,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
